import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { emailLookup } from "../../api/platform";
import { NeuButton, NeuInput } from "../../components/neu/Neu";

const BADGE = "inline-flex items-center rounded-lg px-2 py-0.5 text-xs font-semibold";

const Row = ({ label, value, testid }) => (
  <p className="flex items-baseline gap-2 text-xs" data-testid={testid}>
    <span className="text-muted-foreground">{label}</span>
    <span className="font-mono font-bold">{value}</span>
  </p>
);

// حقل بحث سريع في لوحة المنصة: يقول أين البريد مسجّل (مستخدم / طلب / تواصل شركة).
export const SignupSearch = () => {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const search = async () => {
    if (!q.trim()) return;
    setBusy(true);
    try { setRes(await emailLookup(q.trim())); }
    catch (e) { toast.error(t("errors.generic")); setRes(null); }
    finally { setBusy(false); }
  };

  const found = res && (res.user || res.signup_request || res.company_contact);
  return (
    <div className="neu-in rounded-2xl p-4" data-testid="signup-lookup">
      <p className="mb-2 text-sm font-bold">{t("platformExtra.lookupTitle")}</p>
      <div className="flex flex-wrap items-stretch gap-2">
        <NeuInput type="email" dir="ltr" value={q} placeholder="user@example.com"
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && search()}
                  className="min-w-[220px] flex-1" data-testid="lookup-email" />
        <NeuButton variant="primary" onClick={search} disabled={busy}
                   className="flex items-center gap-2" data-testid="lookup-search">
          <Search className="h-4 w-4" />
          {t("platformExtra.lookupSearch")}
        </NeuButton>
      </div>
      {res ? (
        <div className="mt-3 space-y-2 rounded-xl border border-border/40 p-3" data-testid="lookup-result">
          <p className={`${BADGE} ${found ? "bg-destructive/15 text-destructive" : "bg-emerald-500/15 text-emerald-600"}`}>
            {found ? t("platformExtra.lookupFound") : t("platformExtra.lookupFree")}
          </p>
          {res.user ? (
            <div className="mt-1 space-y-1" data-testid="lookup-user">
              <Row label={t("platformExtra.lookupRole")} value={res.user.role} testid="lookup-role" />
              <Row label={t("platformExtra.lookupCompany")}
                   value={res.user.company?.name || "—"} testid="lookup-company" />
              <Row label={t("platformExtra.lookupStatus")}
                   value={res.user.status || (res.user.active ? "active" : "inactive")} testid="lookup-status" />
            </div>
          ) : null}
          {res.signup_request ? (
            <Row label={t("platformExtra.lookupRequest")}
                 value={`${res.signup_request.status}${res.signup_request.archived ? ` · ${t("platformExtra.lookupArchived")}` : ""} · ${res.signup_request.company_name}`}
                 testid="lookup-request" />
          ) : null}
          {res.company_contact ? (
            <Row label={t("platformExtra.lookupContact")}
                 value={`${res.company_contact.name}${res.company_contact.archived ? ` · ${t("platformExtra.lookupArchived")}` : ""}`}
                 testid="lookup-contact" />
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
